import { formatDate } from '@angular/common';
import { defaultStartDate } from './invoice-day.util';
import { addMonthsIso, todayIso } from './iso-date.util';

/**
 * Earliest dates a contract action may take effect — old CMT parity (EB-4170).
 *
 * Old CMT computes these in two places that AMIE had collapsed into "today" /
 * "yesterday":
 *  - `cancelCustomer.js setMinDate()` for the cancellation date,
 *  - `contractRevisions.js checkDatesForContractChanges()` (L1044-1062) for
 *    changes to a running contract, fed by `contracts.dir.js loadInvoices()`
 *    (L97-111).
 *
 * Applied to the plan change and to both downgrade-to-Basic entry points. NOT
 * applied to the plan discount: old CMT clamps that date in the same block
 * (`contractRevisions.js:1054-1056`), but AMIE deliberately allows a back-dated,
 * retroactive discount with a warning instead (CMB-2953, rework RB.6). Reverting
 * that is a product decision, not a parity fix.
 *
 * All functions take `today` so the rules stay testable across month ends.
 *
 * Invoice source (EB-4170; corrected after the PR #42 review): old CMT lets the *server*
 * narrow the invoice list (`fromDate=<today>` on a legacy endpoint). AMIE now does the
 * same through `InvoiceService.getCurrentInvoices` — `search=customerId:X,basicFeeTo>ISO`,
 * whose `>` is inclusive, matching this file's own `end >= cutoff`. The client-side
 * cut-off in {@link maxInvoiceEndDate} stays as belt-and-braces.
 *
 * This replaces an earlier "accepted deviation" whose safety argument was **wrong in one
 * direction**. It claimed both remaining gaps "can only make AMIE stricter, never looser".
 * That holds for the superset gap — an extra row can only add a floor. It is the opposite
 * for the paging gap: if the current-period row falls outside an unsorted 500-row page,
 * {@link maxInvoiceEndDate} finds no current period and returns `null`,
 * {@link contractChangeDateRules} sets `invoicedPeriodApplies: false`, and the floor
 * collapses back to today — i.e. exactly the EB-4174 defect this file exists to prevent,
 * a plan change taking effect inside an already-invoiced period. Reported by Jana
 * Reznickova; the reasoning chain had closed on itself, because the invoice service
 * pointed here for the justification and this header had the direction backwards.
 *
 * One gap remains and it is the safe direction: the v1 feed is a superset of the legacy
 * one (212 vs 120 rows for customer 78159), so a row old CMT never saw can fall into the
 * current window and add a floor old CMT would not have. That can only make AMIE stricter.
 */

/** Local-midnight copy of `date` — date inputs compare by day, not by instant. */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Add whole months the way moment's `add(n, 'months')` does: clamp to the last
 * day of the target month instead of overflowing into the next one (31 Dec + 2
 * months = 28 Feb, not 3 Mar). The `new Date(y, m + n, d)` constructor overflows,
 * which would make the two-month test below fire up to three days late.
 */
function addMonthsClamped(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDayOfTargetMonth));
  return target;
}

/** Parse a `yyyy-MM-dd` (or ISO) API date into a local-midnight Date; null when unusable. */
export function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return new Date(y, m - 1, d);
}

/** Minimal shape needed from an invoice — matches `Invoice` in the invoice feature. */
export interface InvoiceEndDateSource {
  showReId: number | null | undefined;
  baseFeeEndDate: string | null;
}

/**
 * Old CMT `contracts.dir.js loadInvoices()`: the end of the customer's latest
 * invoiced base-fee period, or null when there is none to respect.
 *
 * Two details that decide whether the whole invoiced-period rule fires:
 *
 * 1. **Only currently-running or future periods count.** Old CMT fetches with
 *    `getInvoices(customerId, new Date())`, and that `fromDate` is a server-side
 *    filter on `baseFeeEndDate` (verified against the test environment: customer
 *    13279 returns 3 invoices for `fromDate=2018-01-01` and none for
 *    `fromDate=<today>`). A customer whose invoiced periods all lie in the past
 *    therefore gets no minimum at all. AMIE filters server-side too, through
 *    `InvoiceService.getCurrentInvoices` — the earlier claim that its endpoint "has no such
 *    parameter" was false, and it was the node that justified the client-only filter this
 *    file's header used to defend (PR #42 re-review). This cut-off stays as a second line of
 *    defence for callers that pass an unfiltered list.
 * 2. **At least one of those invoices must be charged.** Old CMT's
 *    `hasChargedInvoice` (`contracts.dir.js:198-205`) is a truthiness test, so
 *    `undefined` — which `mapV1Invoice` produces when the payload omits
 *    `invoiceNumber` — does NOT count as charged.
 *
 * The result is floored at today because old CMT seeds its accumulator with
 * `new Date()`.
 */
export function maxInvoiceEndDate(
  invoices: readonly InvoiceEndDateSource[] | null | undefined,
  today: Date = new Date(),
): Date | null {
  const cutoff = startOfDay(today);

  const currentPeriods = (invoices ?? []).filter((invoice) => {
    const end = parseApiDate(invoice.baseFeeEndDate);
    return end !== null && end >= cutoff;
  });
  if (!currentPeriods.length) return null;

  const charged = currentPeriods.some((invoice) => !!invoice.showReId);
  if (!charged) return null;

  let max = cutoff;
  for (const invoice of currentPeriods) {
    const end = parseApiDate(invoice.baseFeeEndDate);
    if (end && end > max) max = end;
  }
  return max;
}

export interface ContractChangeDateInput {
  /** `contract.startDate` (yyyy-MM-dd). */
  contractStartDate: string | null | undefined;
  /** `contract.reactivatedStartDate` — takes precedence for the two-month check. */
  reactivatedStartDate?: string | null;
  /** `contract.plan.level` — level 5 (Basic) is exempt from the invoice-driven minimum. */
  planLevel?: number | null;
  /** Result of {@link maxInvoiceEndDate}; null when there is no invoiced period to respect. */
  maxInvoiceEnd?: Date | null;
}

/** Plan level that old CMT exempts from the invoiced-period minimum (Basic). */
const BASIC_PLAN_LEVEL = 5;

export interface ContractChangeRules {
  /** Earliest date the change may take effect. */
  minDate: Date;
  /**
   * Whether the invoiced-period rule actually kicked in. Old CMT only pushes the
   * *prefilled* date up inside that same branch, so callers must not clamp the
   * default when this is false.
   */
  invoicedPeriodApplies: boolean;
  /**
   * Whether the invoiced-period rule *could* apply to this contract at all — it is old
   * enough (outside the two-month cancel period) and not on the exempt Basic plan.
   *
   * Distinct from {@link invoicedPeriodApplies}, which also goes false when there simply is
   * no invoiced period to respect. Callers that want to say something about a failed
   * invoice lookup need this one: on a Basic or three-week-old contract no floor would have
   * existed either way, so warning there is a false alarm (PR #42 re-review defect 3).
   */
  ruleCouldApply: boolean;
}

/**
 * Old CMT `checkDatesForContractChanges()`: the earliest date a change to a
 * running contract may take effect.
 *
 * Base rule: today for a contract that has already started, otherwise its start
 * date — a change can never predate the contract.
 *
 * Invoiced-period rule: once the contract has been running for at least two
 * months (measured from the reactivation date when there is one), an invoiced
 * period is still running, and the plan is not Basic, the change may only take
 * effect *after* that period — `maxInvoiceEnd + 1 day`.
 */
export function contractChangeDateRules(
  input: ContractChangeDateInput,
  today: Date = new Date(),
): ContractChangeRules {
  const todayMidnight = startOfDay(today);
  const start = parseApiDate(input.contractStartDate);
  const baseMin = start && start > todayMidnight ? start : todayMidnight;

  const anchor = parseApiDate(input.reactivatedStartDate) ?? start;

  // Old CMT: `!moment(anchor).add(2, 'months').isAfter(now)` — i.e. the contract
  // has been running for at least two months, so it is outside the cancel period.
  const outsideCancelPeriod = anchor ? addMonthsClamped(anchor, 2) <= todayMidnight : false;
  const exemptBasicPlan = input.planLevel === BASIC_PLAN_LEVEL;
  // Evaluated WITHOUT maxInvoiceEnd on purpose: a caller whose invoice lookup failed still
  // needs to know whether a floor could have existed (defect 3).
  const ruleCouldApply = !!anchor && outsideCancelPeriod && !exemptBasicPlan;

  if (!ruleCouldApply || !input.maxInvoiceEnd) {
    return { minDate: baseMin, invoicedPeriodApplies: false, ruleCouldApply };
  }

  return {
    minDate: new Date(
      input.maxInvoiceEnd.getFullYear(),
      input.maxInvoiceEnd.getMonth(),
      input.maxInvoiceEnd.getDate() + 1,
    ),
    invoicedPeriodApplies: true,
    ruleCouldApply: true,
  };
}

/** Convenience wrapper for callers that only need the minimum. */
export function contractChangeMinDate(
  input: ContractChangeDateInput,
  today: Date = new Date(),
): Date {
  return contractChangeDateRules(input, today).minDate;
}

/**
 * Old CMT pushes the *prefilled* change date up to the minimum when it falls
 * before the last invoiced period (`shortDateIsBeforeShortDate(default, maxInvoiceEnd)`).
 * A default landing exactly on `maxInvoiceEnd` is left alone — mirrored here.
 *
 * Callers must still run the result through {@link atLeastMinDate}: old CMT
 * relies on its datepicker to refuse an out-of-range date, which a native
 * `<input type="date">` does not do.
 */
export function clampChangeDate(
  preferred: Date,
  minDate: Date,
  maxInvoiceEnd?: Date | null,
): Date {
  if (maxInvoiceEnd && preferred < maxInvoiceEnd) return minDate;
  return preferred;
}

/** Never offer a prefilled date below the field's own minimum. */
export function atLeastMinDate(preferred: Date, minDate: Date): Date {
  return preferred < minDate ? minDate : preferred;
}

/**
 * Old CMT `cancelCustomer.js setMinDate()`: cancellations may be back-dated one
 * day, but never before the customer's own start date.
 */
export function cancellationMinDate(
  customerStartDate: string | null | undefined,
  today: Date = new Date(),
): Date {
  const yesterday = startOfDay(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const start = parseApiDate(customerStartDate);
  return start && start > yesterday ? start : yesterday;
}

export interface ContractChangeDateOutcome {
  /** `yyyy-MM-dd`, for the control's `[min]` and the validator. */
  minDateStr: string;
  /** Operator-facing explanation of the floor that actually applied. */
  hint: string;
  /** `yyyy-MM-dd` prefill — apply only while the operator has not edited the control. */
  prefill: string;
}

/**
 * Everything a contract-date field needs, in one place: the minimum, the
 * prefill old CMT would show, and the sentence explaining the floor.
 *
 * Used by the three dialogs that move a contract date under this rule (change
 * plan, Send-to-Basic, Change to Basic) so they cannot drift apart — old CMT
 * drives them from one `checkDatesForContractChanges()` too. The plan-discount
 * dialog is deliberately exempt (see the file header).
 */
export function contractChangeDateOutcome(
  input: ContractChangeDateInput & { invoiceDay: number; invoiceLookupFailed?: boolean },
  today: Date = new Date(),
): ContractChangeDateOutcome {
  const { minDate, invoicedPeriodApplies, ruleCouldApply } = contractChangeDateRules(input, today);
  const preferred = defaultStartDate(input.invoiceDay, today);
  const clamped = clampChangeDate(preferred, minDate, invoicedPeriodApplies ? input.maxInvoiceEnd : null);
  const earliest = `Earliest ${formatDate(minDate, 'dd.MM.yyyy', 'en')}`;

  let hint: string;
  if (invoicedPeriodApplies && input.maxInvoiceEnd) {
    hint = `${earliest} — the period up to `
      + `${formatDate(input.maxInvoiceEnd, 'dd.MM.yyyy', 'en')} is already invoiced.`;
  } else if (input.invoiceLookupFailed && ruleCouldApply) {
    // The dialogs swallow a failing invoice call and carry on with the base minimum, which
    // is right — a broken feed should not block a plan change. Printing the bare "Earliest
    // <date>." for it was not: that reads as "the feed says there is nothing to respect",
    // and the operator backs a change into a still-invoiced period on the strength of it
    // (PR #42 review, roundup #1).
    //
    // Gated on ruleCouldApply, not on the failure alone: on a Basic or younger-than-two-
    // months contract no invoiced-period floor would have existed either way, so warning
    // there is a false alarm — and repeated false alarms are how a correct warning gets
    // ignored, which is the dynamic roundup #1 was filed to fix, inverted (defect 3).
    hint = `${earliest} — invoiced periods could not be checked, so the real earliest date `
      + `may be later.`;
  } else {
    hint = `${earliest}.`;
  }

  return {
    minDateStr: formatDate(minDate, 'yyyy-MM-dd', 'en'),
    prefill: formatDate(atLeastMinDate(clamped, minDate), 'yyyy-MM-dd', 'en'),
    hint,
  };
}

/** The revision fields the move-start-date bounds need. */
export interface RevisionPeriod {
  startDate: string;
  endDate: string | null;
}

export interface MoveStartDateBounds {
  minDate: string;
  maxDate: string;
}

/**
 * Bounds for moving a main contract's start date — old CMT
 * `contractRevisions.js:1094-1111` (EB-4170 review R2-2b, decision 2026-09-04: follow old CMT).
 *
 * Old CMT does NOT bound this by the contract's own start and end, which is what AMIE was
 * doing. It bounds it by *today* below and by a month offset from a derived reference date
 * above:
 *  - the reference is the contract's start date while that is still in the future, and
 *    otherwise the start date of the next revision (or, failing that, of the one running
 *    today) — a contract that already started is moved relative to its current revision, not
 *    to a start date that may be years back;
 *  - the ceiling is that reference plus two months for a contract that has not started yet,
 *    plus one month for one that has.
 *
 * The lower bound is today, full stop: old CMT's `if (today < reference - 2 months)` branch
 * (`:1103-1105`) only ever re-assigns the value `$scope.minDateForNewStartDate` already holds
 * from `:43`, so it cannot move the floor. Reproduced as a plain "today" rather than as the
 * no-op.
 *
 * One deliberate divergence: old CMT dereferences `getRevisionActiveOn(today).startDate`
 * without a null check and throws when a contract has no revision covering today. AMIE falls
 * back to the contract's start date instead.
 *
 * Consequence worth knowing, and old CMT's too: on a long-running contract whose current
 * revision started more than a month ago, the ceiling lands before today and the range is
 * empty — nothing is selectable. What keeps that off the screen in both apps is the
 * `canChangeStartDate` gate: the server only offers the action while moving the start date
 * still makes sense. Widening the rule to avoid the empty range would be a product decision,
 * not a parity fix (EB-4170, decision 2026-09-04: follow old CMT).
 */
export function moveStartDateBounds(
  contractStartDate: string,
  revisions: RevisionPeriod[],
  today: string = todayIso(),
): MoveStartDateBounds {
  const startsInFuture = contractStartDate > today;
  const reference = startsInFuture
    ? contractStartDate
    : (revisionActiveAfter(revisions, today)?.startDate ??
       revisionActiveOn(revisions, today)?.startDate ??
       contractStartDate);

  return {
    minDate: today,
    maxDate: addMonthsIso(reference, startsInFuture ? 2 : 1),
  };
}

/** First revision starting on or after `date` (old CMT `getRevisionActiveAfter`, L840-853). */
function revisionActiveAfter(revisions: RevisionPeriod[], date: string): RevisionPeriod | null {
  return [...revisions]
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .find((r) => r.startDate >= date) ?? null;
}

/** The revision covering `date` (old CMT `getRevisionActiveOn`, L659-671). */
function revisionActiveOn(revisions: RevisionPeriod[], date: string): RevisionPeriod | null {
  return revisions.find((r) => r.startDate <= date && (r.endDate === null || r.endDate >= date)) ?? null;
}
